/**
 * WorldMapData.ts - FF1-style world map data
 * Contains terrain layouts, locations, and encounter zones for the overworld
 */

// Import TerrainType from TilesetConfig to ensure type compatibility
import { TerrainType } from './TilesetConfig';

// Re-export for convenience
export { TerrainType };

// ========== LOCATION TYPES ==========

export enum LocationType {
  TOWN = 'TOWN',
  CASTLE = 'CASTLE',
  CAVE = 'CAVE',
  SHRINE = 'SHRINE',
  DUNGEON = 'DUNGEON',
  SPECIAL = 'SPECIAL',
}

export interface Location {
  id: string;
  name: string;
  type: LocationType;
  x: number;
  y: number;
  description: string;
  requiredItem?: string; // Item needed to enter (e.g., "MYSTIC_KEY")
  bossId?: number; // Enemy ID from EnemyData.sol
  encounterZone?: string; // Which encounter table to use
  shops?: {
    weapons?: boolean;
    armor?: boolean;
    items?: boolean;
    magic?: boolean;
    inn?: boolean;
  };
}

// ========== ENCOUNTER ZONES ==========

export interface EncounterZone {
  id: string;
  name: string;
  minLevel: number;
  maxLevel: number;
  encounterRate: number; // Base rate 0-100
  enemyGroups: number[][]; // Arrays of enemy IDs from EnemyData.sol
  weights: number[]; // Relative probability of each group
}

// ========== WORLD MAP DATA ==========

// Map size: 64x64 tiles (like FF1)
export const WORLD_MAP_WIDTH = 64;
export const WORLD_MAP_HEIGHT = 64;

// Helper function to safely set a terrain tile
function setTerrain(map: TerrainType[][], y: number, x: number, terrain: TerrainType): void {
  const row = map[y];
  if (row) {
    row[x] = terrain;
  }
}

// Helper function to create the initial world map
function createWorldMap(): TerrainType[][] {
  // Initialize with ocean
  const map: TerrainType[][] = Array(WORLD_MAP_HEIGHT)
    .fill(null)
    .map(() => Array(WORLD_MAP_WIDTH).fill(TerrainType.OCEAN));

  // ============================================================================
  // MAIN CONTINENT - All connected by land, forms the primary adventure area
  // ============================================================================

  // ========== CORNELIA REGION (Starting Area) ==========
  // Large grass area around Cornelia (y:6-20, x:6-20)
  fillRect(map, 6, 6, 16, 16, TerrainType.GRASS);

  // Cornelia Castle (y=10, x=10)
  setTerrain(map, 10, 10, TerrainType.CASTLE);

  // Cornelia Town (y=10, x=12)
  setTerrain(map, 10, 12, TerrainType.TOWN);

  // Temple of Fiends (y=8, x=11) - north of town
  setTerrain(map, 8, 11, TerrainType.CAVE);

  // Forest patches in Cornelia region
  fillRect(map, 7, 7, 2, 3, TerrainType.FOREST);
  fillRect(map, 14, 6, 3, 2, TerrainType.FOREST);
  fillRect(map, 12, 17, 2, 3, TerrainType.FOREST);

  // Road from Cornelia south toward Pravoka
  for (let y = 13; y <= 20; y++) {
    setTerrain(map, y, 11, TerrainType.ROAD);
  }

  // ========== PRAVOKA REGION (After bridge) ==========
  // Connected south of Cornelia (y:20-30, x:8-22)
  fillRect(map, 20, 8, 12, 16, TerrainType.GRASS);

  // Pravoka Town (y=23, x=14)
  setTerrain(map, 23, 14, TerrainType.TOWN);

  // Small river with bridge connecting Cornelia to Pravoka
  for (let x = 8; x <= 20; x++) {
    if (x !== 11) { // Leave gap for bridge
      setTerrain(map, 20, x, TerrainType.RIVER);
    }
  }
  setTerrain(map, 20, 11, TerrainType.BRIDGE);

  // Matoya's Cave northeast of Pravoka (y=21, x=19)
  setTerrain(map, 21, 19, TerrainType.CAVE);

  // Shore tile for ship docking at Pravoka
  setTerrain(map, 28, 8, TerrainType.SHORE);

  // ========== PATH TO ELFHEIM (Through marsh and forest) ==========
  // Connect Pravoka to Elfheim region (y:22-32, x:20-38)
  fillRect(map, 22, 20, 12, 20, TerrainType.GRASS);

  // Marsh Cave region (y:26-30, x:20-26) - swampy area
  fillRect(map, 26, 20, 6, 8, TerrainType.SWAMP);
  setTerrain(map, 28, 23, TerrainType.CAVE); // Marsh Cave

  // Forest leading to Elfheim
  fillRect(map, 22, 28, 4, 8, TerrainType.FOREST);

  // ========== ELFHEIM REGION ==========
  // Elven forest area (y:18-32, x:32-42)
  fillRect(map, 18, 32, 16, 12, TerrainType.GRASS);
  fillRect(map, 20, 34, 10, 6, TerrainType.FOREST);

  // Elfheim Town (y=26, x=38)
  setTerrain(map, 26, 38, TerrainType.TOWN);

  // Elven Castle (y=22, x=36)
  setTerrain(map, 22, 36, TerrainType.CASTLE);

  // Connect Elfheim back to marsh area
  fillRect(map, 26, 26, 4, 6, TerrainType.GRASS);

  // ========== WESTERN KEEP (Astos - Connected to main continent) ==========
  // Western peninsula connected by narrow land bridge (y:12-20, x:2-8)
  fillRect(map, 12, 2, 10, 8, TerrainType.GRASS);

  // Land bridge connecting to Cornelia region
  fillRect(map, 14, 6, 4, 4, TerrainType.GRASS);

  // Western Keep Castle (y=16, x=4)
  setTerrain(map, 16, 4, TerrainType.CASTLE);

  // Forest around Western Keep
  fillRect(map, 13, 2, 2, 3, TerrainType.FOREST);
  fillRect(map, 18, 3, 2, 4, TerrainType.FOREST);

  // ========== MELMOND REGION (Earth rot area) ==========
  // Southern area connected via western path (y:32-44, x:4-20)
  fillRect(map, 32, 4, 14, 18, TerrainType.GRASS);

  // Connection from Pravoka region going south-west
  fillRect(map, 30, 8, 4, 8, TerrainType.GRASS);

  // Melmond Town (y=36, x=10)
  setTerrain(map, 36, 10, TerrainType.TOWN);

  // Dead/rotted area near Melmond (darker terrain)
  fillRect(map, 38, 6, 4, 6, TerrainType.SWAMP);

  // Earth Cave entrance (y=40, x=8)
  setTerrain(map, 40, 8, TerrainType.CAVE);

  // Sarda's Cave west of Melmond (y=34, x=5)
  setTerrain(map, 34, 5, TerrainType.CAVE);

  // ========== TITAN'S TUNNEL (Mountain passage) ==========
  // Mountain range separating west from Crescent Lake area
  for (let y = 32; y <= 48; y++) {
    setTerrain(map, y, 18, TerrainType.MOUNTAIN);
    setTerrain(map, y, 19, TerrainType.MOUNTAIN);
  }

  // Titan's Tunnel entrances (y=38)
  setTerrain(map, 38, 17, TerrainType.CAVE); // West entrance
  setTerrain(map, 38, 20, TerrainType.CAVE); // East entrance

  // Path through mountains (grass on both sides of tunnel)
  fillRect(map, 36, 14, 4, 4, TerrainType.GRASS);
  fillRect(map, 36, 20, 4, 4, TerrainType.GRASS);

  // ========== CRESCENT LAKE REGION (Sages) ==========
  // Area east of Titan's tunnel (y:34-48, x:20-32)
  fillRect(map, 34, 20, 16, 14, TerrainType.GRASS);

  // The crescent-shaped lake
  for (let x = 24; x <= 30; x++) {
    setTerrain(map, 42, x, TerrainType.RIVER);
  }
  setTerrain(map, 41, 26, TerrainType.RIVER);
  setTerrain(map, 41, 28, TerrainType.RIVER);
  setTerrain(map, 43, 26, TerrainType.RIVER);
  setTerrain(map, 43, 28, TerrainType.RIVER);

  // Crescent Lake Town (y=40, x=28)
  setTerrain(map, 40, 28, TerrainType.TOWN);

  // Forest around lake
  fillRect(map, 36, 22, 3, 4, TerrainType.FOREST);
  fillRect(map, 44, 24, 2, 6, TerrainType.FOREST);

  // ========== GURGU VOLCANO (Fire Fiend - Kary) ==========
  // Volcanic region southwest (y:46-56, x:4-14) - requires canoe or ship
  fillRect(map, 46, 4, 12, 12, TerrainType.GRASS);
  fillRect(map, 48, 6, 6, 6, TerrainType.MOUNTAIN);

  // Volcano entrance (y=52, x=8)
  setTerrain(map, 52, 8, TerrainType.CAVE);

  // Shore for ship access
  setTerrain(map, 46, 4, TerrainType.SHORE);

  // ============================================================================
  // EASTERN CONTINENT - Requires ship to reach
  // ============================================================================

  // ========== GAIA REGION (Northern town) ==========
  // Northeast area (y:4-18, x:44-58)
  fillRect(map, 4, 44, 16, 16, TerrainType.GRASS);
  fillRect(map, 6, 48, 8, 8, TerrainType.FOREST);

  // Gaia Town (y=10, x=52)
  setTerrain(map, 10, 52, TerrainType.TOWN);

  // Shore for ship docking
  setTerrain(map, 16, 44, TerrainType.SHORE);

  // ========== ICE CAVE (Levistone location) ==========
  // Frozen mountains north of Gaia (y:2-8, x:50-60)
  fillRect(map, 2, 50, 8, 12, TerrainType.SNOW);
  fillRect(map, 3, 52, 4, 6, TerrainType.MOUNTAIN);

  // Ice Cave entrance (y=5, x=56)
  setTerrain(map, 5, 56, TerrainType.CAVE);

  // ========== ONRAC REGION (Underwater shrine access) ==========
  // Eastern coastal area (y:24-38, x:48-60)
  fillRect(map, 24, 48, 16, 14, TerrainType.GRASS);
  fillRect(map, 28, 52, 6, 6, TerrainType.FOREST);

  // Onrac Town (y=32, x=54)
  setTerrain(map, 32, 54, TerrainType.TOWN);

  // Submarine/Sea Shrine entrance (y=36, x=56)
  setTerrain(map, 36, 56, TerrainType.SHRINE);

  // Shore for ship docking
  setTerrain(map, 24, 48, TerrainType.SHORE);

  // Waterfall Cave (y=28, x=50)
  setTerrain(map, 28, 50, TerrainType.CAVE);

  // ============================================================================
  // ISLANDS - Require ship or airship
  // ============================================================================

  // ========== CARDIA ISLANDS (Bahamut - Dragon King) ==========
  // Island chain far east (y:8-16, x:58-62)
  fillRect(map, 8, 58, 4, 4, TerrainType.GRASS);
  fillRect(map, 14, 60, 3, 3, TerrainType.GRASS);

  // Dragon Caves entrance (y=10, x=60) - Bahamut
  setTerrain(map, 10, 60, TerrainType.CAVE);

  // Shore
  setTerrain(map, 11, 58, TerrainType.SHORE);

  // ========== CITADEL OF TRIALS (Class upgrade prerequisite) ==========
  // Island southwest of Crescent Lake (y:48-54, x:32-38)
  fillRect(map, 48, 32, 6, 6, TerrainType.GRASS);

  // Citadel (y=50, x=35)
  setTerrain(map, 50, 35, TerrainType.CASTLE);

  // Shore
  setTerrain(map, 52, 32, TerrainType.SHORE);

  // ========== LUFENIA & MIRAGE TOWER (Late game) ==========
  // Southern island (y:52-62, x:42-54)
  fillRect(map, 52, 42, 10, 14, TerrainType.GRASS);
  fillRect(map, 54, 46, 4, 6, TerrainType.DESERT);

  // Lufenia Town (y=56, x=48)
  setTerrain(map, 56, 48, TerrainType.TOWN);

  // Mirage Tower (y=54, x=44) - leads to Sky Castle
  setTerrain(map, 54, 44, TerrainType.SHRINE);

  // Shore
  setTerrain(map, 52, 42, TerrainType.SHORE);

  // ========== TREASURE ISLAND ==========
  // Small island with hidden treasure (y:40-44, x:38-42)
  fillRect(map, 40, 38, 4, 4, TerrainType.GRASS);
  setTerrain(map, 42, 40, TerrainType.CAVE);
  setTerrain(map, 40, 38, TerrainType.SHORE);

  return map;
}

// Helper function to fill rectangular region
function fillRect(
  map: TerrainType[][],
  startY: number,
  startX: number,
  height: number,
  width: number,
  terrain: TerrainType
): void {
  for (let y = startY; y < startY + height && y < WORLD_MAP_HEIGHT; y++) {
    for (let x = startX; x < startX + width && x < WORLD_MAP_WIDTH; x++) {
      if (y >= 0 && x >= 0) {
        setTerrain(map, y, x, terrain);
      }
    }
  }
}

// Export the world map
export const WORLD_MAP: TerrainType[][] = createWorldMap();

// ========== LOCATIONS ==========
// Coordinates updated to match the new connected world map

export const LOCATIONS: Location[] = [
  // ===== STARTING AREA (Cornelia Region) =====
  {
    id: 'CORNELIA_CASTLE',
    name: 'Cornelia Castle',
    type: LocationType.CASTLE,
    x: 10,
    y: 10,
    description: 'The royal castle where Princess Sarah was held captive by Garland.',
    shops: { items: true, inn: true },
  },
  {
    id: 'CORNELIA',
    name: 'Cornelia',
    type: LocationType.TOWN,
    x: 12,
    y: 10,
    description: 'The peaceful starting town. Buy your first equipment here.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'TEMPLE_OF_FIENDS',
    name: 'Temple of Fiends',
    type: LocationType.DUNGEON,
    x: 11,
    y: 8,
    description: 'An ancient temple corrupted by Garland. Your first trial.',
    bossId: 36, // Garland (tutorial boss)
    encounterZone: 'TEMPLE_FIENDS',
  },

  // ===== WESTERN REGION (Connected to Cornelia) =====
  {
    id: 'WESTERN_KEEP',
    name: 'Western Keep',
    type: LocationType.CASTLE,
    x: 4,
    y: 16,
    description: 'A ruined keep where Astos the Dark Elf lurks.',
    shops: { items: true },
  },

  // ===== PRAVOKA REGION (After crossing bridge) =====
  {
    id: 'PRAVOKA',
    name: 'Pravoka',
    type: LocationType.TOWN,
    x: 14,
    y: 23,
    description: 'A port town once terrorized by pirates. Gain your ship here.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'MATOYA_CAVE',
    name: "Matoya's Cave",
    type: LocationType.CAVE,
    x: 19,
    y: 21,
    description: 'Home of the blind witch Matoya. Trade the Crystal Eye for the Jolt Tonic.',
  },

  // ===== MARSH REGION =====
  {
    id: 'MARSH_CAVE',
    name: 'Marsh Cave',
    type: LocationType.CAVE,
    x: 23,
    y: 28,
    description: 'A treacherous cave hidden in the swamps. Find the Crown here.',
    encounterZone: 'MARSH',
  },

  // ===== ELFHEIM REGION =====
  {
    id: 'ELFHEIM',
    name: 'Elfheim',
    type: LocationType.TOWN,
    x: 38,
    y: 26,
    description: 'The woodland home of the elves. High-level magic sold here.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'ELVEN_CASTLE',
    name: 'Elven Castle',
    type: LocationType.CASTLE,
    x: 36,
    y: 22,
    description: 'The castle of the Elven Prince, cursed to eternal sleep.',
  },

  // ===== MELMOND REGION =====
  {
    id: 'MELMOND',
    name: 'Melmond',
    type: LocationType.TOWN,
    x: 10,
    y: 36,
    description: 'A town suffering from the earth rotting beneath it.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'EARTH_CAVE',
    name: 'Earth Cave',
    type: LocationType.DUNGEON,
    x: 8,
    y: 40,
    description: 'The lair of Lich, the Earth Fiend. Restore the Earth Crystal.',
    bossId: 39, // Lich
    encounterZone: 'EARTH_CAVE',
    requiredItem: 'STAR_RUBY',
  },
  {
    id: 'SARDAS_CAVE',
    name: "Sarda's Cave",
    type: LocationType.CAVE,
    x: 5,
    y: 34,
    description: 'Home of the great sage Sarda. He holds the Rod needed to defeat the Vampire.',
  },
  {
    id: 'TITANS_TUNNEL',
    name: "Titan's Tunnel",
    type: LocationType.CAVE,
    x: 17,
    y: 38,
    description: 'A tunnel through the mountains, blocked by a hungry Titan.',
    requiredItem: 'RUBY',
  },

  // ===== CRESCENT LAKE REGION =====
  {
    id: 'CRESCENT_LAKE',
    name: 'Crescent Lake',
    type: LocationType.TOWN,
    x: 28,
    y: 40,
    description: 'A town of wise sages who know the locations of the Orbs.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },

  // ===== VOLCANO REGION (Ship required) =====
  {
    id: 'GURGU_VOLCANO',
    name: 'Gurgu Volcano',
    type: LocationType.DUNGEON,
    x: 8,
    y: 52,
    description: 'A fiery volcano where Kary, the Fire Fiend, resides.',
    bossId: 40, // Kary
    encounterZone: 'VOLCANO',
  },

  // ===== EASTERN CONTINENT (Ship required) =====
  {
    id: 'GAIA',
    name: 'Gaia',
    type: LocationType.TOWN,
    x: 52,
    y: 10,
    description: 'A remote town in the far north. Find the Faerie who speaks of Oxyale.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      inn: true,
    },
  },
  {
    id: 'ICE_CAVE',
    name: 'Ice Cave',
    type: LocationType.CAVE,
    x: 56,
    y: 5,
    description: 'A frozen cavern. Find the Levistone to access the airship.',
    encounterZone: 'ICE_CAVE',
  },
  {
    id: 'ONRAC',
    name: 'Onrac',
    type: LocationType.TOWN,
    x: 54,
    y: 32,
    description: 'A seaside resort town. Gateway to the underwater shrine.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'SEA_SHRINE',
    name: 'Sea Shrine',
    type: LocationType.SHRINE,
    x: 56,
    y: 36,
    description: 'An underwater temple where Kraken, the Water Fiend, dwells.',
    bossId: 41, // Kraken
    encounterZone: 'SEA_SHRINE',
    requiredItem: 'OXYALE',
  },
  {
    id: 'WATERFALL_CAVE',
    name: 'Waterfall Cave',
    type: LocationType.CAVE,
    x: 50,
    y: 28,
    description: 'A secret cave behind a waterfall. Find the Robot here.',
    encounterZone: 'WATERFALL',
  },

  // ===== ISLANDS (Ship/Airship required) =====
  {
    id: 'DRAGON_CAVES',
    name: 'Dragon Caves',
    type: LocationType.CAVE,
    x: 60,
    y: 10,
    description: 'The lair of Bahamut, King of Dragons. Bring the Rat\'s Tail to upgrade your classes.',
    encounterZone: 'CARDIA',
  },
  {
    id: 'CITADEL_OF_TRIALS',
    name: 'Citadel of Trials',
    type: LocationType.DUNGEON,
    x: 35,
    y: 50,
    description: 'A test of courage. Retrieve the Rat\'s Tail to prove your worth to Bahamut.',
    encounterZone: 'CITADEL',
  },
  {
    id: 'LUFENIA',
    name: 'Lufenia',
    type: LocationType.TOWN,
    x: 48,
    y: 56,
    description: 'An ancient town of a lost civilization. Most powerful equipment.',
    shops: {
      weapons: true,
      armor: true,
      items: true,
      magic: true,
      inn: true,
    },
  },
  {
    id: 'MIRAGE_TOWER',
    name: 'Mirage Tower',
    type: LocationType.SHRINE,
    x: 44,
    y: 54,
    description: 'A mysterious tower that appears in the desert. Gateway to the Sky Castle.',
    encounterZone: 'MIRAGE_TOWER',
    requiredItem: 'CHIME',
  },
  {
    id: 'SKY_CASTLE',
    name: 'Sky Castle',
    type: LocationType.DUNGEON,
    x: 44,
    y: 54, // Same location as Mirage Tower (floats above)
    description: 'The flying fortress of Tiamat, the Wind Fiend.',
    bossId: 42, // Tiamat
    encounterZone: 'SKY_CASTLE',
  },
  {
    id: 'TREASURE_CAVE',
    name: 'Treasure Cave',
    type: LocationType.CAVE,
    x: 40,
    y: 42,
    description: 'A cave filled with treasure chests.',
  },

  // ===== FINAL DUNGEON =====
  {
    id: 'TEMPLE_FIENDS_REVISITED',
    name: 'Temple of Fiends (Past)',
    type: LocationType.DUNGEON,
    x: 11,
    y: 8, // Same as original Temple
    description: 'Travel 2000 years into the past to face the Four Fiends and Chaos.',
    encounterZone: 'TEMPLE_PAST',
    requiredItem: 'ALL_CRYSTALS', // Pseudo-item representing all 4 crystals lit
  },
];

// ========== ENCOUNTER ZONES ==========

export const ENCOUNTER_ZONES: Record<string, EncounterZone> = {
  // Early game - Cornelia region
  CORNELIA_FIELDS: {
    id: 'CORNELIA_FIELDS',
    name: 'Cornelia Plains',
    minLevel: 1,
    maxLevel: 3,
    encounterRate: 8,
    enemyGroups: [
      [1], // Goblin
      [1, 1], // 2 Goblins
      [2], // Wolf
      [2, 2], // 2 Wolves
      [1, 2], // Goblin + Wolf
    ],
    weights: [30, 25, 20, 15, 10],
  },

  TEMPLE_FIENDS: {
    id: 'TEMPLE_FIENDS',
    name: 'Temple of Fiends',
    minLevel: 2,
    maxLevel: 4,
    encounterRate: 12,
    enemyGroups: [
      [3], // Zombie
      [3, 3], // 2 Zombies
      [4], // Skeleton
      [4, 4], // 2 Skeletons
      [3, 4], // Zombie + Skeleton
      [10], // Imp
    ],
    weights: [25, 20, 20, 15, 15, 5],
  },

  // Coastal region
  COAST: {
    id: 'COAST',
    name: 'Coastal Region',
    minLevel: 3,
    maxLevel: 6,
    encounterRate: 10,
    enemyGroups: [
      [5], // Pirate
      [5, 5], // 2 Pirates
      [6], // Shark
      [5, 6], // Pirate + Shark
      [7], // Spider
    ],
    weights: [30, 20, 20, 15, 15],
  },

  MARSH: {
    id: 'MARSH',
    name: 'Marsh Cave',
    minLevel: 4,
    maxLevel: 8,
    encounterRate: 15,
    enemyGroups: [
      [8], // Snake
      [8, 8], // 2 Snakes
      [9], // Scorpion
      [7, 8], // Spider + Snake
      [3, 3, 3], // 3 Zombies
    ],
    weights: [25, 20, 20, 20, 15],
  },

  // Mid game
  FOREST: {
    id: 'FOREST',
    name: 'Forest',
    minLevel: 5,
    maxLevel: 10,
    encounterRate: 12,
    enemyGroups: [
      [11], // Ogre
      [2, 2, 2], // 3 Wolves
      [7, 7], // 2 Spiders
      [13], // Troll
    ],
    weights: [30, 30, 25, 15],
  },

  EARTH_CAVE: {
    id: 'EARTH_CAVE',
    name: 'Earth Cave',
    minLevel: 10,
    maxLevel: 15,
    encounterRate: 15,
    enemyGroups: [
      [11, 11], // 2 Ogres
      [13], // Troll
      [14], // Vampire
      [15], // Mummy
      [23], // Gargoyle
    ],
    weights: [25, 25, 20, 15, 15],
  },

  VOLCANO: {
    id: 'VOLCANO',
    name: 'Gurgu Volcano',
    minLevel: 15,
    maxLevel: 20,
    encounterRate: 18,
    enemyGroups: [
      [12], // Giant
      [24], // Golem
      [19], // Manticore
      [20], // Chimera
      [27], // Red Dragon
    ],
    weights: [30, 25, 20, 15, 10],
  },

  ICE_CAVE: {
    id: 'ICE_CAVE',
    name: 'Ice Cave',
    minLevel: 15,
    maxLevel: 20,
    encounterRate: 15,
    enemyGroups: [
      [16], // Wraith
      [17], // Specter
      [18], // Mindflayer
      [26], // Blue Dragon
    ],
    weights: [35, 30, 20, 15],
  },

  // Late game
  SEA_SHRINE: {
    id: 'SEA_SHRINE',
    name: 'Sea Shrine',
    minLevel: 20,
    maxLevel: 25,
    encounterRate: 20,
    enemyGroups: [
      [21], // Medusa
      [22], // Cockatrice
      [31], // Death Knight
      [32], // Dark Wizard
      [28], // Green Dragon
    ],
    weights: [25, 25, 20, 15, 15],
  },

  MIRAGE_TOWER: {
    id: 'MIRAGE_TOWER',
    name: 'Mirage Tower',
    minLevel: 22,
    maxLevel: 28,
    encounterRate: 18,
    enemyGroups: [
      [30], // Iron Golem
      [34], // Phantom
      [35], // Death Eye
      [36], // Evil Eye
    ],
    weights: [35, 30, 20, 15],
  },

  SKY_CASTLE: {
    id: 'SKY_CASTLE',
    name: 'Sky Castle',
    minLevel: 25,
    maxLevel: 30,
    encounterRate: 20,
    enemyGroups: [
      [37], // Sorcerer
      [38], // Demon
      [33], // Warmech (rare!)
      [26, 27], // Blue + Red Dragon
    ],
    weights: [40, 35, 5, 20],
  },

  WATERFALL: {
    id: 'WATERFALL',
    name: 'Waterfall Cave',
    minLevel: 18,
    maxLevel: 22,
    encounterRate: 15,
    enemyGroups: [
      [30], // Iron Golem
      [25], // Dragon Zombie
      [29], // Tyrannosaur
    ],
    weights: [40, 35, 25],
  },

  CITADEL: {
    id: 'CITADEL',
    name: 'Citadel of Trials',
    minLevel: 20,
    maxLevel: 26,
    encounterRate: 18,
    enemyGroups: [
      [31], // Death Knight
      [32], // Dark Wizard
      [30], // Iron Golem
      [23, 23], // 2 Gargoyles
      [17, 17], // 2 Specters
    ],
    weights: [25, 25, 20, 15, 15],
  },

  CARDIA: {
    id: 'CARDIA',
    name: 'Cardia Islands',
    minLevel: 28,
    maxLevel: 35,
    encounterRate: 25,
    enemyGroups: [
      [33], // Warmech
      [38, 38], // 2 Demons
      [26, 27, 28], // All dragons
    ],
    weights: [10, 45, 45],
  },

  // Final dungeon
  TEMPLE_PAST: {
    id: 'TEMPLE_PAST',
    name: 'Temple of Fiends (Past)',
    minLevel: 30,
    maxLevel: 40,
    encounterRate: 22,
    enemyGroups: [
      [31, 32], // Death Knight + Dark Wizard
      [37, 37], // 2 Sorcerers
      [38, 34], // Demon + Phantom
      [35, 36], // Death Eye + Evil Eye
      [33], // Warmech (rare encounter)
    ],
    weights: [30, 30, 20, 15, 5],
  },

  // Ocean encounters (requires ship/canoe/airship)
  OCEAN: {
    id: 'OCEAN',
    name: 'Ocean',
    minLevel: 8,
    maxLevel: 15,
    encounterRate: 12,
    enemyGroups: [
      [6], // Shark
      [6, 6], // 2 Sharks
      [5, 5, 5], // 3 Pirates
      [6, 6, 6], // 3 Sharks
    ],
    weights: [35, 30, 20, 15],
  },

  DESERT: {
    id: 'DESERT',
    name: 'Desert',
    minLevel: 12,
    maxLevel: 18,
    encounterRate: 10,
    enemyGroups: [
      [9, 9], // 2 Scorpions
      [19], // Manticore
      [22], // Cockatrice
      [25], // Dragon Zombie
    ],
    weights: [40, 25, 20, 15],
  },
};

// ========== TERRAIN ENCOUNTER MAPPING ==========

// Map terrain types to encounter zones
export function getEncounterZoneForTerrain(
  terrain: TerrainType,
  x: number,
  y: number
): string | null {
  // Check if we're near a specific location first
  const nearLocation = getNearestLocation(x, y, 3);
  if (nearLocation?.encounterZone) {
    return nearLocation.encounterZone;
  }

  // Default encounter zones by terrain
  switch (terrain) {
    case TerrainType.GRASS:
      // Determine region based on position
      if (y < 25) return 'CORNELIA_FIELDS';
      if (x > 40) return 'FOREST';
      return 'CORNELIA_FIELDS';

    case TerrainType.FOREST:
      return 'FOREST';

    case TerrainType.SWAMP:
      return 'MARSH';

    case TerrainType.DESERT:
      return 'DESERT';

    case TerrainType.OCEAN:
      return 'OCEAN';

    default:
      return null; // No encounters on this terrain
  }
}

// ========== HELPER FUNCTIONS ==========

// Get location at specific coordinates
export function getLocationAt(x: number, y: number): Location | null {
  return LOCATIONS.find(loc => loc.x === x && loc.y === y) ?? null;
}

// Get nearest location within radius
export function getNearestLocation(
  x: number,
  y: number,
  maxRadius: number
): Location | null {
  let nearest: Location | null = null;
  let nearestDist = Infinity;

  for (const loc of LOCATIONS) {
    const dist = Math.sqrt((loc.x - x) ** 2 + (loc.y - y) ** 2);
    if (dist <= maxRadius && dist < nearestDist) {
      nearest = loc;
      nearestDist = dist;
    }
  }

  return nearest;
}

// Check if terrain is walkable
export function isTerrainWalkable(terrain: TerrainType): boolean {
  switch (terrain) {
    case TerrainType.GRASS:
    case TerrainType.FOREST:
    case TerrainType.DESERT:
    case TerrainType.SWAMP:
    case TerrainType.ROAD:
    case TerrainType.BRIDGE:
    case TerrainType.TOWN:
    case TerrainType.CASTLE:
    case TerrainType.CAVE:
    case TerrainType.SHRINE:
      return true;

    case TerrainType.OCEAN:
    case TerrainType.MOUNTAIN:
    case TerrainType.RIVER:
      return false; // Requires special items (ship, canoe, etc.)

    default:
      return false;
  }
}

// Get terrain at coordinates
export function getTerrainAt(x: number, y: number): TerrainType {
  if (x < 0 || x >= WORLD_MAP_WIDTH || y < 0 || y >= WORLD_MAP_HEIGHT) {
    return TerrainType.OCEAN;
  }
  const row = WORLD_MAP[y];
  const terrain = row?.[x];
  return terrain !== undefined ? terrain : TerrainType.OCEAN;
}

// Get all locations of a specific type
export function getLocationsByType(type: LocationType): Location[] {
  return LOCATIONS.filter(loc => loc.type === type);
}

// Check if player can enter location (has required item)
export function canEnterLocation(location: Location, inventory: string[]): boolean {
  if (!location.requiredItem) return true;
  return inventory.includes(location.requiredItem);
}
